import { FadeLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Box, Card, Grid, Chip, Divider, Container, Typography, Link, Stack, Button } from '@mui/material';
import Page from '../../components/Page';
import tracker from '../../api/tracker';
import { fDateOnly } from '../../utils/formatTime';
import Iconify from '../../components/Iconify';

// ---------------------------------------------------------------------

export default function CarrouselView() {
  const { hashid } = useParams();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [carrousel, setCarrousel] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    const response = await tracker.get(`carrousels/${hashid}`);
    setCarrousel(response.data.data);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [hashid]);

  const d = carrousel;

  return (
    <Page title={t('carrousel_view') || 'Ver Carrusel'}>
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={4}>
          <Typography variant="h4" fontWeight={800} sx={{ color: '#032C65' }}>
            {t('carrousel_detail') || 'Detalle del Carrusel'}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              component={RouterLink}
              to="/dashboard/carrousel"
              startIcon={<Iconify icon="eva:arrow-back-fill" />}
              sx={{ borderRadius: 2, borderColor: '#032C65', color: '#032C65' }}
            >
              {t('go_back')}
            </Button>
            <Button
              variant="contained"
              component={RouterLink}
              to={`/dashboard/carrousel/edit/${hashid}`}
              startIcon={<Iconify icon="eva:edit-fill" />}
              sx={{ background: '#032C65', '&:hover': { background: '#054a9e' }, borderRadius: 2 }}
            >
              {t('edit')}
            </Button>
          </Stack>
        </Stack>

        {loading || !d ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <FadeLoader color="#032C65" cssOverride={{ margin: '0 auto' }} loading={loading} />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Image - takes most of the width */}
            <Grid item xs={12} md={8}>
              <Card sx={{ p: 2, borderRadius: 3, boxShadow: '0 1px 8px 0 rgba(3,44,101,0.07)' }}>
                <Box
                  sx={{
                    width: '100%',
                    borderRadius: 2,
                    overflow: 'hidden',
                    backgroundColor: '#f5f5f5',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 350,
                  }}
                >
                  {d.picture ? (
                    <Box
                      component="img"
                      src={d.picture}
                      alt={d.title}
                      sx={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: 550,
                        objectFit: 'contain',
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t('no_image') || 'Sin imagen'}
                    </Typography>
                  )}
                </Box>
              </Card>
            </Grid>

            {/* Info */}
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, borderRadius: 3, boxShadow: '0 1px 8px 0 rgba(3,44,101,0.07)', height: '100%' }}>
                <Typography variant="h5" fontWeight={700} sx={{ mb: 3, color: '#032C65' }}>
                  {d.title}
                </Typography>

                <Divider sx={{ mb: 2 }} />

                {d.body && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
                      {t('description')}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                      {d.body}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
                    {t('created_at')}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {fDateOnly(d.created_at, i18n.language)}
                  </Typography>
                </Box>

                {d.active !== undefined && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
                      {t('status')}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={d.active ? t('active') : t('inactive')}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          color: d.active ? '#6ca181' : '#FF003F',
                          backgroundColor: d.active ? '#dff8e7' : '#F8E3E1',
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Card>
            </Grid>
          </Grid>
        )}
      </Container>
    </Page>
  );
}